import { useEffect, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowRight,
  CircleDot,
  LockKeyhole,
  ScanSearch,
  ShieldCheck,
  Waypoints,
} from 'lucide-react';
import { buildHumanEvidenceReport, type HumanEvidenceReport } from '@upl/human-report';
import { PRODUCT_TAGLINE } from '@upl/result-language';
import { Dropzone } from './components/Dropzone.js';
import { ProcessingView } from './components/ProcessingView.js';
import { ResultReport } from './components/ResultReport.js';
import { ProviderConsent, type ProviderCheckState } from './components/ProviderConsent.js';
import { MeasurementConsent } from './components/MeasurementConsent.js';
import { LOCAL_RUNTIME_CAPABILITIES, SERVER_RUNTIME_CAPABILITIES } from './lib/capabilities.js';
import { InputValidationError } from './lib/file-validation.js';
import { verifyLocalImage, type VerificationProgress } from './lib/verifier.js';
import { checkOpenAiProvenance, getOpenAiProviderAvailability, ProviderCheckError, type OpenAiProviderAvailability } from './lib/provider-check.js';
import {
  emitProbeEvent,
  beginMeasuredVerification,
  getMeasurementConsent,
  setMeasurementConsent,
  type ConsentState,
} from './lib/probe-telemetry.js';

type ViewState =
  | { kind: 'idle' }
  | { kind: 'processing'; progress: VerificationProgress }
  | { kind: 'result'; report: HumanEvidenceReport; localReport: HumanEvidenceReport; file: File; provider: ProviderCheckState }
  | { kind: 'error'; message: string };

const initialProgress: VerificationProgress = { stage: 'validating', label: 'Validating file structure' };

export default function App() {
  const [state, setState] = useState<ViewState>({ kind: 'idle' });
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [consent, setConsent] = useState<ConsentState>('unavailable');
  const [providerAvailability, setProviderAvailability] = useState<OpenAiProviderAvailability>('UNKNOWN');
  const providerRequestActive = useRef(false);

  useEffect(() => {
    void getMeasurementConsent().then(setConsent);
    void getOpenAiProviderAvailability().then(setProviderAvailability);
  }, []);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const verify = async (file: File): Promise<void> => {
    const measured = await beginMeasuredVerification();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const nextPreview = URL.createObjectURL(file);
    setPreviewUrl(nextPreview);
    setState({ kind: 'processing', progress: initialProgress });
    try {
      const result = await verifyLocalImage(
        file,
        (progress) => setState({ kind: 'processing', progress }),
        measured?.verificationContext,
      );
      const report = buildHumanEvidenceReport(result, measured ? SERVER_RUNTIME_CAPABILITIES : LOCAL_RUNTIME_CAPABILITIES);
      setState({ kind: 'result', report, localReport: report, file, provider: { kind: 'idle' } });
      globalThis.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      void emitProbeEvent({
        event_type: 'VERIFICATION_FAILED',
        interaction_id: measured?.interactionId ?? crypto.randomUUID(),
        terminal_result: error instanceof InputValidationError ? 'INVALID_INPUT' : 'PROCESSING_ERROR',
      });
      const message = error instanceof InputValidationError
        ? error.message
        : 'This image could not be inspected safely. Try a different JPEG or PNG file.';
      setState({ kind: 'error', message });
    }
  };

  const beginProviderCheck = (): void => {
    setState((current) => current.kind === 'result' ? { ...current, provider: { kind: 'confirming' } } : current);
  };

  const cancelProviderCheck = (): void => {
    setState((current) => current.kind === 'result' ? { ...current, provider: { kind: 'idle' } } : current);
  };

  const confirmProviderCheck = async (): Promise<void> => {
    if (providerRequestActive.current || state.kind !== 'result') return;
    providerRequestActive.current = true;
    const requestState = state;
    setState({ ...requestState, provider: { kind: 'running' } });
    try {
      const result = await checkOpenAiProvenance(requestState.file, crypto.randomUUID());
      const report = buildHumanEvidenceReport(result, SERVER_RUNTIME_CAPABILITIES);
      setState({ ...requestState, report, provider: { kind: 'complete' } });
      setProviderAvailability('AVAILABLE');
    } catch (error) {
      if (error instanceof ProviderCheckError && error.code === 'PROVIDER_NOT_CONFIGURED') {
        setProviderAvailability('NOT_CONFIGURED');
        setState({ ...requestState, provider: { kind: 'not_configured' } });
      } else {
        setState({ ...requestState, provider: { kind: 'error', message: 'The optional provider check could not complete. The local result remains unchanged.' } });
      }
    } finally {
      providerRequestActive.current = false;
    }
  };

  const reset = (): void => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(undefined);
    setState({ kind: 'idle' });
  };

  const isLanding = state.kind === 'idle' || state.kind === 'error';

  const decideMeasurement = async (decision: 'granted' | 'denied'): Promise<void> => {
    const accepted = await setMeasurementConsent(decision);
    setConsent(accepted ? decision : 'unavailable');
  };

  const openMeasurementSettings = (): void => {
    if (consent === 'granted') void decideMeasurement('denied');
    else setConsent('unset');
  };

  return (
    <div className={`app-shell${isLanding ? ' app-shell--landing' : ''}`}>
      <header className="site-header">
        <a className="brand" href="/" aria-label="ORIGNTD home">
          <span className="brand__lockup" aria-hidden="true">
            <img src="/brand/origntd-lockup-horizontal.png" alt="ORIGNTD" />
          </span>
        </a>
        <nav aria-label="Primary navigation">
          {isLanding && <a href="#verify">Verify</a>}
          {isLanding && <a href="#methodology">Method</a>}
          {isLanding && <a href="#questions">Questions</a>}
          {isLanding && <a href="/what-is-c2pa/">Guides</a>}
          <span><i /> Local-first default</span>
        </nav>
      </header>

      <main className={isLanding ? 'landing-main' : 'workspace-main'}>
        {state.kind === 'processing' && <ProcessingView progress={state.progress} previewUrl={previewUrl} />}
        {state.kind === 'result' && (
          <>
            <ResultReport
              report={state.report}
              localReport={state.localReport}
              providerState={state.provider}
              providerControl={(
                <ProviderConsent
                  state={state.provider}
                  availability={providerAvailability}
                  onBegin={beginProviderCheck}
                  onCancel={cancelProviderCheck}
                  onConfirm={() => void confirmProviderCheck()}
                />
              )}
              previewUrl={previewUrl}
              onReset={reset}
              onFile={(file) => void verify(file)}
            />
          </>
        )}
        {isLanding && (
          <>
            <section className="hero" id="verify">
              <div className="hero__ambient" aria-hidden="true">
                <img src="/brand/origntd-symbol-glow.png" alt="ORIGNTD provenance symbol" />
              </div>
              <div className="hero__copy">
                <div className="hero__kicker"><span />Free digital provenance checker</div>
                <div className="hero__sequence">Verify · Resolve · Remember</div>
                <h1>Make origin<br /><em>recoverable.</em></h1>
                <p>Inspect supported provenance locally, understand which signals were checked, and see when ORIGNTD can resolve evidence that no longer travels with the image.</p>
                <div className="hero__actions">
                  <a className="primary-action" href="#verification-workspace">Verify an image <ArrowRight size={17} /></a>
                  <span><LockKeyhole size={16} /> Default verification stays on this device.</span>
                </div>
                <div className="hero__standards" aria-label="Supported verification capabilities">
                  <span><b>01</b> JPEG + PNG</span>
                  <span><b>02</b> C2PA</span>
                  <span><b>03</b> EXIF / XMP / IPTC</span>
                </div>
              </div>

              <div className="hero__utility" id="verification-workspace">
                <div className="utility-header">
                  <span><ScanSearch size={18} /> Verification workspace</span>
                  <span className="live-dot">Local</span>
                </div>
                {state.kind === 'error' && <div className="error-banner" role="alert">{state.message}</div>}
                <Dropzone onFile={(file) => void verify(file)} />
                <div className="utility-specs">
                  <span>JPEG / PNG</span><span>25 MB max</span><span>40 MP max</span>
                </div>
              </div>
            </section>

            <section className="assurance-strip" aria-label="Product assurances">
              <div><ScanSearch /><span><b>Verify</b>Inspect supported embedded signals on this device.</span></div>
              <div><Waypoints /><span><b>Resolve</b>Look beyond the file only through an explicit path.</span></div>
              <div><CircleDot /><span><b>Remember</b>Recover registered provenance after transformation.</span></div>
              <div><ShieldCheck /><span><b>Evaluate</b>Apply versioned technical policies to evidence.</span></div>
            </section>

            <section className="methodology" id="methodology">
              <div className="methodology__intro">
                <span className="eyebrow">How ORIGNTD works</span>
                <h2>One artifact.<br />A bounded evidence view.</h2>
                <p>{PRODUCT_TAGLINE}</p>
                <a href="#verify">Start a verification <ArrowDown size={15} /></a>
              </div>
              <div className="methodology__steps">
                <article>
                  <span><ScanSearch /></span><small>01 / Verify</small>
                  <h3>Read supported signals</h3>
                  <p>Validate the file, then inspect Content Credentials and a deliberately bounded metadata subset locally.</p>
                </article>
                <article>
                  <span><Waypoints /></span><small>02 / Resolve</small>
                  <h3>Recover what can be found</h3>
                  <p>Use ORIGNTD identity and repository paths to resolve recoverable provenance, while labeling no match and not checked separately.</p>
                </article>
                <article>
                  <span><ShieldCheck /></span><small>03 / Remember</small>
                  <h3>Preserve the evidence boundary</h3>
                  <p>Report what ORIGNTD remembered, what the artifact carried, and what remains unknown without inventing authorship or truth.</p>
                </article>
              </div>
            </section>

            <section className="evidence-principles" id="principles" aria-labelledby="principles-heading">
              <div className="evidence-principles__visual" aria-hidden="true">
                <img src="/brand/origntd-symbol-glow.png" alt="ORIGNTD provenance symbol" />
              </div>
              <div className="evidence-principles__content">
                <span className="eyebrow">Designed against false certainty</span>
                <h2 id="principles-heading">Provenance, integrity, and trust are not the same thing.</h2>
                <p>ORIGNTD keeps each dimension visible so one strong signal cannot hide a missing or contradictory one.</p>
                <dl>
                  <div><dt>Provenance</dt><dd>What origin and history signals are attached?</dd></div>
                  <div><dt>Integrity</dt><dd>Do the credential and file binding validate?</dd></div>
                  <div><dt>Trust</dt><dd>What does the active trust policy establish?</dd></div>
                </dl>
              </div>
            </section>

            <section className="search-questions" id="questions" aria-labelledby="questions-heading">
              <div className="search-questions__intro">
                <span className="eyebrow">Before you trust an image</span>
                <h2 id="questions-heading">What can a provenance check actually tell you?</h2>
                <p>ORIGNTD reads supported evidence and keeps missing evidence, failed checks, and unsupported claims separate.</p>
                <a href="#verify">Check an image for free <ArrowRight size={15} /></a>
              </div>
              <div className="search-questions__grid">
                <article>
                  <h3><a href="/content-credentials-guide/">How do I check C2PA Content Credentials?</a></h3>
                  <p>Drop a JPEG or PNG into the checker. ORIGNTD inspects supported C2PA data and reports whether a credential was found and whether its file binding validates.</p>
                </article>
                <article>
                  <h3><a href="/is-this-image-ai/">Can ORIGNTD prove that an image is AI-generated?</a></h3>
                  <p>No. It can report supported provenance signals, including AI-related declarations when present. Missing provenance does not prove human authorship.</p>
                </article>
                <article>
                  <h3><a href="/image-metadata-viewer/">Is my image uploaded?</a></h3>
                  <p>Default verification stays in your browser. An external provider path is used only after a separate, explicit confirmation.</p>
                </article>
                <article>
                  <h3><a href="/what-is-c2pa/">What does “no supported evidence found” mean?</a></h3>
                  <p>Only that the checks performed did not find supported provenance evidence. It is not a verdict about authenticity, ownership, or factual truth.</p>
                </article>
              </div>
            </section>
          </>
        )}
      </main>

      <footer className="site-footer">
        <div className="site-footer__brand">
          <img src="/brand/origntd-symbol-glow.png" alt="ORIGNTD provenance symbol" aria-hidden="true" />
          <span><strong>ORIGNTD</strong><small>The provenance layer for digital artifacts.</small></span>
        </div>
        <p>Provenance does not establish factual truth. Absence of provenance does not establish human authorship.</p>
        <div className="site-footer__meta">
          <span>Schema v1.0.0</span><span>Local-first default</span><span>Evidence boundaries visible</span>
          <button type="button" className="footer-privacy-button" onClick={openMeasurementSettings}>
            {consent === 'granted' ? 'Disable optional measurement' : 'Measurement privacy'}
          </button>
        </div>
      </footer>
      <MeasurementConsent state={consent} onDecision={(decision) => void decideMeasurement(decision)} />
    </div>
  );
}
